import { BarChart3, Download, LineChart, Rocket, Settings2, TrendingUp, Users } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const TABS = [
    { path: '/admin/analytics/overview', label: 'Overview', icon: BarChart3 },
    { path: '/admin/analytics/traction', label: 'Traction', icon: TrendingUp },
    { path: '/admin/analytics/revenue', label: 'Revenue', icon: LineChart },
    { path: '/admin/analytics/activation', label: 'Activation', icon: Rocket },
    { path: '/admin/analytics/operations', label: 'Operations', icon: Settings2 },
    { path: '/admin/analytics/tip-distribution', label: 'Distribuzione', icon: Users },
    { path: '/admin/analytics/export', label: 'Export', icon: Download }
];

const QUICK_RANGES = [
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
    { label: '1y', days: 365 },
    { label: 'All', days: null }
];

function toDateInputValue(date) {
    return date.toISOString().split('T')[0];
}

function getActiveRange(filters) {
    const todayStr = toDateInputValue(new Date());
    if (filters.to !== todayStr) return null;
    if (filters.from === '2020-01-01') return 'All';
    for (const r of QUICK_RANGES) {
        if (r.days === null) continue;
        const from = new Date();
        from.setDate(from.getDate() - r.days);
        if (filters.from === toDateInputValue(from)) return r.label;
    }
    return null;
}

export default function AnalyticsLayout({ filters, onFiltersChange, children }) {
    const location = useLocation();
    const activeRange = getActiveRange(filters);

    function handleDateChange(field, value) {
        onFiltersChange({ ...filters, [field]: value });
    }

    function handleQuickRange(days) {
        const to = new Date();
        const from = new Date();
        if (days === null) {
            // All time: use a date before the app existed
            onFiltersChange({
                ...filters,
                from: '2020-01-01',
                to: toDateInputValue(to)
            });
        } else {
            from.setDate(from.getDate() - days);
            onFiltersChange({
                ...filters,
                from: toDateInputValue(from),
                to: toDateInputValue(to)
            });
        }
    }

    return (
        <div style={{ padding: '24px', maxWidth: '1400px' }}>
            {/* Page header */}
            <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <BarChart3 size={24} color="#6366f1" />
                    <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>
                        Growth Analytics
                    </h1>
                </div>
                <p style={{ margin: 0, color: '#64748b', fontSize: '0.875rem' }}>
                    KPI operativi, traction, revenue e salute del sistema
                </p>
            </div>

            {/* Tab navigation */}
            <div
                className="analytics-tabs-nav"
                style={{
                    display: 'flex',
                    gap: '2px',
                    borderBottom: '1px solid #e2e8f0',
                    marginBottom: '20px'
                }}
            >
                {TABS.map(tab => {
                    const Icon = tab.icon;
                    const isActive = location.pathname === tab.path;
                    return (
                        <Link
                            key={tab.path}
                            to={tab.path}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '10px 16px',
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                fontWeight: isActive ? 600 : 400,
                                color: isActive ? '#6366f1' : '#64748b',
                                borderBottom: isActive ? '2px solid #6366f1' : '2px solid transparent',
                                marginBottom: '-1px',
                                whiteSpace: 'nowrap',
                                transition: 'color 0.15s'
                            }}
                        >
                            <Icon size={15} />
                            {tab.label}
                        </Link>
                    );
                })}
            </div>

            {/* Date range filter */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap',
                padding: '12px 16px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                marginBottom: '24px'
            }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#475569' }}>Periodo:</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                        type="date"
                        value={filters.from || ''}
                        onChange={e => handleDateChange('from', e.target.value)}
                        style={{
                            padding: '5px 8px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            color: '#1e293b',
                            background: '#fff'
                        }}
                    />
                    <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>→</span>
                    <input
                        type="date"
                        value={filters.to || ''}
                        onChange={e => handleDateChange('to', e.target.value)}
                        style={{
                            padding: '5px 8px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            color: '#1e293b',
                            background: '#fff'
                        }}
                    />
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                    {QUICK_RANGES.map(r => {
                        const isActive = activeRange === r.label;
                        return (
                            <button
                                key={r.label}
                                onClick={() => handleQuickRange(r.days)}
                                style={{
                                    padding: '4px 10px',
                                    border: isActive ? '1.5px solid #6366f1' : '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    background: isActive ? '#eef2ff' : '#fff',
                                    fontSize: '0.75rem',
                                    color: isActive ? '#4f46e5' : '#475569',
                                    cursor: 'pointer',
                                    fontWeight: isActive ? 600 : 500
                                }}
                            >
                                {r.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Page content */}
            {children}
        </div>
    );
}
